import ePub from "epubjs";
// import EPUBJS from "epubjs";
import { renderer } from "./pinch-zoom-renderer";

export function init() {
    const ereaderEls = document.querySelectorAll(".js-ereader");

    if (!ereaderEls) {
        return;
    }

    ereaderEls.forEach((el) => {
        let ereader = new EReader(el);

        ereader.setup();
    });
}

class EReader {
    constructor(ereader) {
        this.ereader = ereader;
        this.book = new ePub(ereader.dataset.file);
        this.id = ereader.dataset.id;
        this.fileId = ereader.dataset.fileId;

        this.scale = 1;
        this.defaultScale = 1;
        this.minScale = 0.5;
        this.maxScale = 3;
        this.scaleInterval = 0.25;
        this.scaleZoomState = "idle";

        this.fontSize = 100;
        this.maxFontSize = 140;
        this.minFontSize = 60;
        this.lineSpacing = 1.5;
    }

    setup() {
        const _this = this;
        const renderCanvas = this.ereader.querySelector(".js-ereader-canvas");

        // const bookCover = this.book.coverUrl().then((url) => {
        //     console.log(url);
        // });
        this.rendition = this.book.renderTo(renderCanvas, {
            width: "100%",
            height: "100%",
            minSpreadWidth: 640,
            resizeOnOrientationChange: true,
        });

        // Display the book
        // Check if we have a saved location
        const savedLocation = localStorage.getItem(
                `epub-currentLocation-${this.fileId}`
            ),
            location = savedLocation
                ? JSON.parse(savedLocation).start.cfi
                : undefined,
            displayed = this.rendition.display(location);

        // Setup controls
        this.book.ready.then(() => {
            _this.calculateMetadata();

            _this.setupChapterMenu();

            _this.setupNavigation();

            _this.setupZoom();

            _this.setupFullscreen();

            _this.setupFontControls();

            _this.setupThemes();
        });

        // Save location when page changes
        this.rendition.on("relocated", (section) => {
            _this.saveLocation();
            _this.setProgress(section);
        });

        this.rendition.on("rendered", (section) => {
            _this.setActiveChapter(section);
        });
    }

    calculateMetadata() {
        this.metadata = this.book.package.metadata;
        this.spine = this.book.package.spine;
        this.epubType = this.metadata.layout || "reflowable";

        // Set Document Title
        this.ereader.querySelector(".js-ereader-title").textContent =
            this.metadata.title ?? "";

        // Set Author
        // this.ereader.querySelector(".js-ereader-author").innerText =
        //     metadata.creator;
    }

    setActiveChapter(section) {
        const _this = this;

        // Set active chapter in chapter menu
        const chapterMenuList = _this.ereader.querySelector(
            ".js-ereader-chapter-menu-list"
        );

        const oldActiveSections = chapterMenuList.querySelectorAll(".active");
        oldActiveSections.forEach((link) => {
            link.classList.remove("active");
        });

        const active = chapterMenuList.querySelector(
            'a[href="' + section.href + '"]'
        );

        if (active) {
            active.classList.add("active");
        }
    }

    setProgress(section) {
        const _this = this;
        const currentLocation = _this.rendition.currentLocation();

        _this.totalPages =
            _this.epubType === "pre-paginated"
                ? _this.book.spine.length
                : currentLocation.start.displayed.total;

        _this.currentPagesStart =
            _this.epubType === "pre-paginated"
                ? currentLocation.start.index + 1 // 0-based index so need to +1
                : currentLocation.start.displayed.page;

        _this.currentPagesEnd =
            _this.epubType === "pre-paginated"
                ? currentLocation.end.index + 1 // 0-based index so need to +1
                : currentLocation.end.displayed.page;

        _this.progress = Math.round(
            (_this.currentPagesStart / _this.totalPages) * 100
        );

        const currentPagesStartEl = _this.ereader.querySelector(
            ".js-ereader-current-pages-start"
        );
        const currentPagesEndEl = _this.ereader.querySelector(
            ".js-ereader-current-pages-end"
        );
        const currentPagesSeparatorEl = _this.ereader.querySelector(
            ".js-ereader-current-pages-separator"
        );

        // Set current pages
        currentPagesStartEl.innerText = _this.currentPagesStart;
        currentPagesEndEl.innerText = _this.currentPagesEnd;

        // Hide end page if it's the same as the start page
        if (this.currentPagesStart === this.currentPagesEnd) {
            currentPagesEndEl.classList.add("hidden");
            currentPagesSeparatorEl.classList.add("hidden");
        } else {
            currentPagesEndEl.classList.remove("hidden");
            currentPagesSeparatorEl.classList.remove("hidden");
        }

        // Set total pages
        _this.ereader.querySelector(".js-ereader-total-pages").innerText =
            _this.totalPages;

        // Set progress
        _this.ereader.querySelector(
            ".js-ereader-progress"
        ).innerText = `${_this.progress}%`;

        // Update navigation button visibliity
        if (currentLocation.atEnd) {
            _this.ereader
                .querySelector(".js-ereader-next")
                .classList.add("hidden");
        } else {
            _this.ereader
                .querySelector(".js-ereader-next")
                .classList.remove("hidden");
        }

        if (currentLocation.atStart) {
            _this.ereader
                .querySelector(".js-ereader-prev")
                .classList.add("hidden");
        } else {
            _this.ereader
                .querySelector(".js-ereader-prev")
                .classList.remove("hidden");
        }
    }

    setupChapterMenu() {
        const _this = this;
        const chapterMenu = this.ereader.querySelector(
            ".js-ereader-chapter-menu"
        );
        const chapterMenuList = chapterMenu.querySelector(
            ".js-ereader-chapter-menu-list"
        );
        const chapterMenuBtn = this.ereader.querySelector(
            ".js-ereader-chapter-menu-trigger"
        );

        // Dynamically build the chapter menu
        this.book.loaded.navigation.then((toc) => {
            // If there is nothing in the TOC, hide the chapter menu button
            if (!toc.length) {
                chapterMenuBtn.classList.add("hidden");
                return;
            }

            toc.forEach((chapter) => {
                const listItem = document.createElement("li");
                const link = document.createElement("a");

                link.href = chapter.href;
                link.textContent = chapter.label;
                link.classList.add("transition-opacity", "hover:opacity-50");

                listItem.appendChild(link);
                chapterMenuList.appendChild(listItem);

                link.addEventListener("click", (e) => {
                    e.preventDefault();

                    _this.rendition.display(chapter.href);
                    _this.saveLocation();
                    chapterMenu.classList.remove("open");
                });
            });
        });

        // When the chapter menu button is clicked, toggle the menu
        chapterMenuBtn.addEventListener("click", () => {
            chapterMenu.classList.toggle("!translate-x-0");
        });
    }

    setupFullscreen() {
        const _this = this,
            fullscreenBtn = this.ereader.querySelector(
                ".js-ereader-fullscreen"
            ),
            fullscreenIcon = this.ereader.querySelector(
                ".js-ereader-fullscreen-icon"
            ),
            fullscreenExitIcon = this.ereader.querySelector(
                ".js-ereader-fullscreen-exit-icon"
            );

        // If fullscreen available on the device, show the fullscreen button
        if (document.fullscreenEnabled) {
            fullscreenBtn.classList.remove("hidden");
        }

        _this.ereader.addEventListener("fullscreenchange", () => {
            if (document.fullscreenElement) {
                fullscreenIcon.classList.add("hidden");
                fullscreenExitIcon.classList.remove("hidden");

                // Get aspect ratio of container
                // const aspectRatio = `${_this.renderContainer.offsetWidth} / ${_this.renderContainer.offsetHeight}`;
                const aspectRatio = window.getComputedStyle(
                    _this.renderContainer
                ).aspectRatio;

                // Set aspect ratio of canvas
                _this.renderCanvas.style.aspectRatio = aspectRatio;

                // Add fullscreen class to container
                _this.renderContainer.classList.add(
                    "ereader__container--fullscreen"
                );

                if (aspectRatio === "auto") {
                    _this.renderContainer.classList.add(
                        "ereader__container--fullscreen-no-aspect-ratio"
                    );
                }

                setTimeout(() => {
                    // Calculate top offset of cavnas within container
                    const topOffset =
                        (_this.renderContainer.offsetHeight -
                            _this.renderCanvas.offsetHeight) /
                        2;

                    // Set top offset of canvas
                    _this.renderCanvas.style.top = `${topOffset}px`;
                }, 1000);
            } else {
                fullscreenIcon.classList.remove("hidden");
                fullscreenExitIcon.classList.add("hidden");

                _this.renderContainer.classList.remove(
                    "ereader__container--fullscreen",
                    "ereader__container--fullscreen-no-aspect-ratio"
                );

                // Reset aspect ratio and top position of canvas
                _this.renderCanvas.style.aspectRatio = "auto";
                _this.renderCanvas.style.top = "auto";
            }
        });

        fullscreenBtn.addEventListener("click", () => {
            if (!document.fullscreenElement) {
                _this.ereader.requestFullscreen();
            } else {
                document.exitFullscreen();
            }
        });
    }

    setupZoom() {
        const _this = this;

        _this.zoomInBtn = _this.ereader.querySelector(".js-ereader-zoom-in");
        _this.zoomOutBtn = _this.ereader.querySelector(".js-ereader-zoom-out");
        _this.zoomResetBtn = _this.ereader.querySelector(
            ".js-ereader-zoom-reset"
        );
        _this.zoomInput = _this.ereader.querySelector(".js-ereader-zoom-input");

        _this.renderContainer = _this.ereader.querySelector(
            ".js-ereader-container"
        );
        _this.renderCanvas = _this.ereader.querySelector(".js-ereader-canvas");

        // Only show zoom controls if the epub is a fixed/pre-paginated layout
        if (_this.epubType !== "pre-paginated") {
            _this.zoomInBtn.classList.add("hidden");
            _this.zoomOutBtn.classList.add("hidden");

            if (_this.zoomResetBtn) {
                _this.zoomResetBtn.classList.add("hidden");
            }

            if (_this.zoomInput) {
                _this.zoomInput.classList.add("hidden");
            }

            return;
        }

        // Zoom in
        _this.zoomInBtn.addEventListener("click", () => {
            _this.scale =
                _this.scale >= _this.maxScale
                    ? _this.maxScale
                    : Math.round((_this.scale + _this.scaleInterval) * 100) /
                      100;

            _this.setZoom();
        });

        // Zoom out
        _this.zoomOutBtn.addEventListener("click", () => {
            _this.scale =
                _this.scale <= _this.minScale
                    ? _this.minScale
                    : Math.round((_this.scale - _this.scaleInterval) * 100) /
                      100;

            _this.setZoom();
        });

        // Reset zoom
        if (_this.zoomResetBtn) {
            _this.zoomResetBtn.addEventListener("click", () => {
                _this.scale = _this.defaultScale;
                _this.setZoom();
            });
        }

        // Zoom Custom Input
        if (_this.zoomInput) {
            _this.zoomInput.addEventListener("keyup", (e) => {
                e.stopPropagation();
            });

            _this.zoomInput.addEventListener("change", (e) => {
                // Format inputted value
                let zoomVal = e.target.value;

                // If there's a % sign, remove it
                if (zoomVal.includes("%")) {
                    zoomVal = zoomVal.replace("%", "");
                }

                // Convert to decimal value
                zoomVal = zoomVal / 100;

                // Check value is between min and max range
                if (zoomVal < _this.minScale) {
                    zoomVal = _this.minScale;
                } else if (zoomVal > _this.maxScale) {
                    zoomVal = _this.maxScale;
                }

                _this.scale = zoomVal;
                _this.setZoom();
            });
        }

        _this.zoomPanInstance = renderer({
            container: _this.renderContainer,
            minScale: _this.minScale,
            maxScale: _this.maxScale,
            element: _this.renderCanvas,
            scaleSensitivity: 20,
        });

        // Touch Events
        _this.renderContainer.addEventListener(
            "touchstart",
            (e) => {
                _this.onTouchStart(e);
            },
            {
                passive: false,
            }
        );
        _this.renderContainer.addEventListener(
            "touchmove",
            (e) => {
                _this.onTouchMove(e);
            },
            {
                passive: false,
            }
        );
        _this.renderContainer.addEventListener(
            "touchend",
            (e) => {
                _this.onTouchEnd(e);
            },
            {
                passive: false,
            }
        );
        // _this.renderContainer.addEventListener("touchcancel", this.zoomOnEndTouch, {
        //     passive: false,
        // });

        // Pointer Events
        _this.renderContainer.addEventListener(
            "pointerdown",
            (e) => {
                _this.onPointerDown(e);
            },
            {
                passive: false,
            }
        );
        _this.renderContainer.addEventListener(
            "pointermove",
            (e) => {
                _this.onPointerMove(e);
            },
            {
                passive: false,
            }
        );
        _this.renderContainer.addEventListener(
            "pointerup",
            (e) => {
                _this.onPointerUp(e);
            },
            {
                passive: false,
            }
        );
        // _this.renderContainer.addEventListener(
        //     "pointerleave",
        //     (e) => {
        //         _this.onPointerEnd(e);
        //     },
        //     {
        //         passive: false,
        //     }
        // );
        // _this.renderContainer.addEventListener(
        //     "pointerout",
        //     (e) => {
        //         _this.onPointerEnd(e);
        //     },
        //     {
        //         passive: false,
        //     }
        // );
        // _this.renderContainer.addEventListener("wheel", _this.zoomOnWheel, {
        //     passive: false,
        // });
    }

    getPinchDistance(event) {
        return Math.hypot(
            event.touches[0].pageX - event.touches[1].pageX,
            event.touches[0].pageY - event.touches[1].pageY
        );
    }

    getMidPoint(event) {
        return {
            x: (event.touches[0].pageX + event.touches[1].pageX) / 2,
            y: (event.touches[0].pageY + event.touches[1].pageY) / 2,
        };
    }

    onDoubleTap(x, y) {
        const _this = this;

        if (_this.scale < _this.maxScale) {
            _this.zoomPanInstance.zoomTo({ newScale: _this.maxScale, x, y });
            return _this.maxScale;
        } else {
            _this.zoomPanInstance.reset();
            return _this.minScale;
        }
    }

    setZoom() {
        const _this = this;

        // Set X and Y to 50% of the container
        const x = _this.renderContainer.offsetWidth / 2;
        const y = _this.renderContainer.offsetHeight / 2;

        // If zoomed greater than defaultZoom,change cursor
        // on container to move
        if (_this.scale > _this.defaultScale) {
            _this.renderContainer.classList.add("cursor-move");
        } else {
            _this.renderContainer.classList.remove("cursor-move");
        }

        // Rest zoom/pan if scale is at 1
        if (_this.scale === _this.defaultScale) {
            // Disable zoom reset button
            if (_this.zoomResetBtn) {
                _this.zoomResetBtn.classList.remove("!inline-block");
            }

            _this.zoomPanInstance.reset();
        } else {
            // Enable zoom reset button
            if (_this.zoomResetBtn) {
                _this.zoomResetBtn.classList.add("!inline-block");
            }

            _this.zoomPanInstance.zoomTo({ newScale: _this.scale, x, y });
        }

        // Enable/Disable zoom buttons
        if (_this.scale !== _this.minScale) {
            // Enable zoom out button
            _this.zoomOutBtn.classList.remove(
                "opacity-40",
                "cursor-not-allowed"
            );
        } else {
            // Disable zoom out button
            _this.zoomOutBtn.classList.add("opacity-40", "cursor-not-allowed");
        }

        if (_this.scale !== _this.maxScale) {
            // Enable zoom in button
            _this.zoomInBtn.classList.remove(
                "opacity-40",
                "cursor-not-allowed"
            );
        } else {
            // Disable zoom in button
            _this.zoomInBtn.classList.add("opacity-40", "cursor-not-allowed");
        }

        // Update the zoom input value
        if (_this.zoomInput) {
            _this.zoomInput.value = `${Math.round(_this.scale * 100)}%`;
        }
    }

    // zoomOnStart(event) {
    //     const _this = this;
    //     _this.deviceHasTouch = true;

    //     if (_this.state === "multiGesture") {
    //         return;
    //     }

    //     const touchCount = event.touches.length;

    //     if (
    //         touchCount === 2 &&
    //         (_this.state === "idle" || _this.state === "singleGesture")
    //     ) {
    //         const { x, y } = getMidPoint(event);

    //         _this.start.x = x;
    //         _this.start.y = y;
    //         _this.start.distance = getPinchDistance(event) / _this.scale;
    //         _this.start.touches = [event.touches[0], event.touches[1]];

    //         _this.lastTapTime = 0; // Reset to prevent misinterpretation as a double tap
    //         _this.state = "multiGesture";
    //         return;
    //     }

    //     if (touchCount !== 1) {
    //         _this.state = "idle";
    //         return;
    //     }

    //     _this.state = "singleGesture";

    //     const [touch] = event.touches;

    //     _this.start.x = touch.pageX;
    //     _this.start.y = touch.pageY;
    //     _this.start.distance = 0;
    //     _this.start.touches = [touch];
    // }

    // zoomOnMove(event) {
    //     const _this = this;

    //     if (_this.state === "idle") {
    //         return;
    //     }

    //     const touchCount = event.touches.length;

    //     if (_this.state === "multiGesture" && touchCount === 2) {
    //         event.preventDefault();
    //         const scale = getPinchDistance(event) / _this.start.distance;

    //         const { x, y } = getMidPoint(event);

    //         _this.zoomPanInstance.zoomPan({
    //             scale,
    //             x,
    //             y,
    //             deltaX: x - _this.start.x,
    //             deltaY: y - _this.start.y,
    //         });

    //         _this.start.x = x;
    //         _this.start.y = y;
    //         return;
    //     }

    //     if (
    //         _this.scale === _this.minScale ||
    //         _this.state !== "singleGesture" ||
    //         touchCount !== 1 ||
    //         event.touches[0]?.identifier !== start.touches[0]?.identifier
    //     ) {
    //         return;
    //     }

    //     event.preventDefault();

    //     const [touch] = event.touches;

    //     const deltaX = touch.pageX - _this.start.x;
    //     const deltaY = touch.pageY - _this.start.y;

    //     _this.zoomPanInstance.panBy({ originX: deltaX, originY: deltaY });

    //     _this.start.x = touch.pageX;
    //     _this.start.y = touch.pageY;
    // }

    // zoomOnEndTouch(event) {
    //     const _this = this;

    //     if (_this.state === 'idle' || event.touches.length !== 0) {
    //         return;
    //     }

    //     const currentTime = new Date().getTime();
    //     const tapLength = currentTime - _this.lastTapTime;

    //     if (tapLength < 185 && tapLength > 0) {
    //         event.preventDefault();

    //         const [touch] = event.changedTouches;

    //         if (!touch) {
    //             return;
    //         }

    //         this.scale = onDoubleTap(touch.clientX, touch.clientY);
    //     }

    //     _this.lastTapTime = currentTime;
    //     _this.scale = _this.zoomPanInstance.getScale();
    //     _this.state = 'idle';
    // }

    onTouchStart(event) {
        const _this = this;
        const touch = event.changedTouches[0];

        _this.touchStartEvent = event;

        _this.startTouch = {
            x: touch.pageX,
            y: touch.pageY,
        };
    }

    onTouchMove(event) {
        const _this = this;

        // If the scale is not at the minimum, then pan
        if (_this.scale > _this.defaultScale) {
            event.preventDefault();

            const touch = event.changedTouches[0];

            const deltaX = touch.pageX - _this.startTouch.x;
            const deltaY = touch.pageY - _this.startTouch.y;

            _this.zoomPanInstance.panBy({ originX: deltaX, originY: deltaY });

            _this.startTouch.x = touch.pageX;
            _this.startTouch.y = touch.pageY;
        }
    }

    onTouchEnd(event) {
        const _this = this,
            swipeThreshold = 0.25;

        // If we're at the default/minscale and
        // this is a touch device then
        // a swipe should navigate the epub
        if (_this.scale <= _this.defaultScale) {
            // if (_this.scale === _this.minScale) {
            const bound = _this.renderContainer.getBoundingClientRect();
            const hr =
                (event.changedTouches[0].screenX -
                    _this.touchStartEvent.changedTouches[0].screenX) /
                bound.width;
            const vr = Math.abs(
                (event.changedTouches[0].screenY -
                    _this.touchStartEvent.changedTouches[0].screenY) /
                    bound.height
            );

            if (hr > swipeThreshold && vr < 0.1) {
                _this.rendition.prev();
            }

            if (hr < -swipeThreshold && vr < 0.1) {
                _this.rendition.next();
            }
        }
    }

    onPointerDown(event) {
        const _this = this;
    }

    onPointerMove(event) {
        const _this = this;

        if (event.pointerType === "touch") {
            return;
        }

        // If this isn't a touch device and there's no mouse button pressed
        // or the scale is at the minimum, then return
        if (event.buttons !== 1 || _this.scale === _this.minScale) {
            return;
        }

        if (event.movementX === 0 && event.movementY === 0) {
            return;
        }

        // _this.state = "mouse";

        // If the scale is not at the minimum, then pan
        if (_this.scale !== _this.minScale) {
            _this.zoomPanInstance.panBy({
                originX: event.movementX,
                originY: event.movementY,
            });
        }
    }

    onPointerEnd(event) {
        const _this = this;
        // const swipeThreshold = 0.25;

        // console.log("pointerend");
        // console.log(_this.scale, _this.minScale);

        // // If we're at the default/minscale and
        // // this is a touch device then
        // // a swipe should navigate the epub
        // if (_this.scale === _this.minScale && event.pointerType === "touch") {
        //     // if (_this.scale === _this.minScale) {
        //     const bound = _this.renderContainer.getBoundingClientRect();
        //     const hr =
        //         (event.screenX - _this.pointerStartEvent.screenX) / bound.width;
        //     const vr = Math.abs(
        //         (event.screenY - _this.pointerStartEvent.screenY) / bound.height
        //     );
        //     console.log("end screenX", event.screenX);
        //     console.log("hr", hr);
        //     console.log("vr", vr);
        //     if (hr > swipeThreshold && vr < 0.1) {
        //         _this.rendition.prev();
        //     }

        //     if (hr < -swipeThreshold && vr < 0.1) {
        //         _this.rendition.next();
        //     }
        // }

        if (event.pointerType === "touch") {
            return;
        }

        _this.state = "idle";
        _this.scale = _this.zoomPanInstance.getScale();
    }

    onPointerUp(event) {
        const _this = this;

        if (event.pointerType === "touch") {
            return;
        }

        _this.onPointerEnd(event);
    }

    setupFontControls() {
        const _this = this;
        const fontControlsTrigger = this.ereader.querySelector(
            ".js-ereader-font-controls-trigger"
        );
        const fontControls = this.ereader.querySelector(
            ".js-ereader-font-controls"
        );
        const fontIncreaseBtn = this.ereader.querySelector(
            ".js-ereader-font-size-increase"
        );
        const fontDecreaseBtn = this.ereader.querySelector(
            ".js-ereader-font-size-decrease"
        );
        // const lineSpacingBtn = this.ereader.querySelector(
        //     ".js-ereader-line-spacing"
        // );
        // const lineSpacingNarrowIcon = lineSpacingBtn.querySelector(
        //     ".js-ereader-line-height-narrow-icon"
        // );
        // const lineSpacingWideIcon = lineSpacingBtn.querySelector(
        //     ".js-ereader-line-height-wide-icon"
        // );

        // Only font controls if the epub is NOT a fixed/pre-paginated layout
        if (this.epubType === "pre-paginated") {
            fontControlsTrigger.classList.add("hidden");
            return;
        }

        fontControlsTrigger.addEventListener("click", (e) => {
            e.stopPropagation();
            fontControls.classList.toggle("hidden");
        });

        fontIncreaseBtn.addEventListener("click", () => {
            _this.fontSize =
                _this.fontSize >= _this.maxFontSize
                    ? _this.maxFontSize
                    : _this.fontSize + 20;

            _this.setFontSize();
        });

        fontDecreaseBtn.addEventListener("click", () => {
            _this.fontSize =
                _this.fontSize <= _this.minFontSize
                    ? _this.minFontSize
                    : _this.fontSize - 20;

            _this.setFontSize();
        });

        // Line spacing
        // lineSpacingBtn.addEventListener("click", () => {
        //     _this.lineSpacing = _this.lineSpacing === 1.5 ? 2 : 1.5;

        //     // Toggle Icons
        //     lineSpacingNarrowIcon.classList.toggle("hidden");
        //     lineSpacingWideIcon.classList.toggle("hidden");

        //     _this.setLineSpacing();
        // });
    }

    setFontSize() {
        this.rendition.themes.fontSize(`${this.fontSize}%`);
    }

    setLineSpacing() {
        this.rendition.themes.override("line-height", `${this.lineSpacing}`);
    }

    setupThemes() {
        // Only theme switching if the epub is NOT a fixed/pre-paginated layout
        if (this.epubType === "pre-paginated") {
            // Check if there's a theme being passed in a data attribute
            const theme = this.ereader.dataset.theme ?? "pre-paginated";

            this.rendition.themes.register(
                theme,
                `${location.origin}/css/ereader-themes/themes.css`
            );

            this.setTheme(theme);

            return;
        }

        const themeInputs = this.ereader.querySelectorAll(
            `.js-ereader-theme[name='ereader-theme-${this.id}']`
        );

        // Customise default theme
        this.rendition.themes.register(
            "unset",
            `${location.origin}/css/ereader-themes/themes.css`
        );
        this.rendition.themes.register(
            "light",
            `${location.origin}/css/ereader-themes/themes.css`
        );
        this.rendition.themes.register(
            "dark",
            `${location.origin}/css/ereader-themes/themes.css`
        );
        this.rendition.themes.register(
            "sbs",
            `${location.origin}/css/ereader-themes/themes.css`
        );

        themeInputs.forEach((input) => {
            input.addEventListener("change", (e) => {
                if (input.checked) {
                    this.setTheme(input.value);
                    this.saveTheme(input.value);
                }
            });
        });

        // Check for a saved theme in local storage, fallback to 'default' if none exists
        const savedTheme = localStorage.getItem(`epub-theme-${this.fileId}`);

        if (savedTheme) {
            this.setTheme(savedTheme);

            // Change active input
            themeInputs.forEach((input) => {
                if (input.value === savedTheme) {
                    input.checked = true;
                } else {
                    input.checked = false;
                }
            });
        } else {
            this.setTheme("unset");
        }
    }

    setTheme(theme) {
        this.rendition.themes.select(theme);
    }

    saveTheme(theme) {
        // Save current theme in local storage
        localStorage.setItem(`epub-theme-${this.fileId}`, theme);
    }

    setupNavigation() {
        const _this = this;
        const nextBtn = this.ereader.querySelector(".js-ereader-next");
        const prevBtn = this.ereader.querySelector(".js-ereader-prev");

        nextBtn.addEventListener("click", () => {
            _this.rendition.next();
        });

        prevBtn.addEventListener("click", () => {
            _this.rendition.prev();
        });

        // When left or right keys pressed, trigger page navigation
        // if reader is on screen
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        document.addEventListener("keyup", handleKeyUp);
                    } else {
                        document.removeEventListener("keyup", handleKeyUp);
                    }
                });
            },
            { threshold: 0.5 }
        );

        observer.observe(this.ereader);

        function handleKeyUp(e) {
            if (e.key === "ArrowRight") {
                _this.rendition.next();
            } else if (e.key === "ArrowLeft") {
                _this.rendition.prev();
            }
        }
    }

    saveLocation() {
        const currentLocation = this.rendition.currentLocation();

        // Save current location in local storage
        localStorage.setItem(
            `epub-currentLocation-${this.fileId}`,
            JSON.stringify(currentLocation)
        );
    }
}
